import { Button } from 'antd';
import * as React from 'react';
import { useState } from 'react';
import OWButton from '../../../components/ow_button/OWButton';
import { AgentApprovalOutcomeReason, AgentTeamRequestKind } from '../../../types/agentHarness';
import { cn } from '../../../utils/ClassNamesUtils';
import { useOscarTheme } from '../../context/OscarRoleContext';
import OscarDisclosure from '../shared/OscarDisclosure';

interface OscarTeamRequestCardProps {
	/** The `send_team_request` call's arguments, exactly as the model sent them. */
	args: unknown;
	resolved?: boolean;
	approved?: boolean;
	/**
	 * Why a settled card did not get a yes. `missing`: the harness no longer held
	 * it — it may have been sent from another tab — so it never reads "Not sent.".
	 */
	reason?: AgentApprovalOutcomeReason;
	/** Resolves false when the decision did not reach the harness, so the card unlocks. */
	onResolve?: (approved: boolean) => void | Promise<boolean>;
	/** A decision covering this card is already in flight elsewhere. */
	locked?: boolean;
	className?: string;
}

interface TeamRequestArgs {
	kind: AgentTeamRequestKind;
	request: string;
	details: string;
}

const KIND_LINES: { [kind in AgentTeamRequestKind]: string } = {
	missing_capability:
		"Oscar can't do this yet. Want to send it to the OpenWrench team as a request?",
	unanswered: "Oscar couldn't answer this. Want to send it to the OpenWrench team?",
};

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The model's arguments, read defensively: the card must render whatever arrives. */
const readArgs = (args: unknown): TeamRequestArgs => {
	const raw = args && typeof args === 'object' ? (args as { [key: string]: unknown }) : {};
	return {
		kind: raw.kind === 'unanswered' ? 'unanswered' : 'missing_capability',
		request: asText(raw.request),
		details: asText(raw.details),
	};
};

/**
 * The approval card for `send_team_request`: Oscar could not do what was asked
 * and offers to pass it to the OpenWrench team.
 *
 * It does not use the generic approval card, which deliberately reads "Oscar
 * wants to run …" with Approve / Reject because approving a write is not
 * sending a message. Here it is exactly that — the call posts the request to
 * the team — so "Send request" says what the click does. Who is asking is not
 * in the arguments: the harness attaches the login's name, company and page,
 * and the footnote says so, because that is what the reader is agreeing to share.
 */
export const OscarTeamRequestCard = ({
	args,
	resolved,
	approved,
	reason,
	onResolve,
	locked,
	className,
}: OscarTeamRequestCardProps) => {
	const theme = useOscarTheme();
	// Same guard as the generic card: both controls stay locked from the first
	// click until the decision lands, so one card can never send two answers.
	const [deciding, setDeciding] = useState(false);
	const { kind, request, details } = readArgs(args);

	const decide = (send: boolean) => {
		if (!onResolve || deciding || locked) {
			return;
		}
		setDeciding(true);
		Promise.resolve(onResolve(send))
			.then((landed) => {
				if (landed === false) {
					setDeciding(false);
				}
			})
			.catch(() => setDeciding(false));
	};
	const decline = () => decide(false);
	const send = () => decide(true);

	return (
		<div
			className={cn(
				'flex w-full flex-col gap-xs rounded p-xs',
				theme.surface,
				theme.stroke,
				className
			)}
		>
			<span className="bodymedium text-grey-10">Request for the OpenWrench team</span>
			<span className="bodyregular text-grey-9">{KIND_LINES[kind]}</span>
			<div className="flex w-full min-w-0 flex-col gap-xs rounded bg-white p-xs">
				<p className="bodyregular m-0 whitespace-pre-wrap break-words text-grey-9">{request}</p>
				{details ? (
					<OscarDisclosure label="Oscar's note">
						<p className="footnoteregular m-0 whitespace-pre-wrap break-words text-character-secondary">
							{details}
						</p>
					</OscarDisclosure>
				) : null}
			</div>
			{/* Settled wording, never "Sending…": this card only knows the decision.
			    Delivery is the separate sent card, and a failed post is Oscar's to
			    explain — a progress label here would read as stuck forever either way. */}
			{resolved ? (
				<span className="footnoteregular text-character-secondary">
					{approved
						? 'Request approved.'
						: reason === 'missing'
						? 'No longer pending.'
						: 'Not sent.'}
				</span>
			) : (
				<>
					<span className="footnoteregular text-character-secondary">
						Your name, email, company and this page are included so the team can follow up by email.
					</span>
					<div className="flex items-center justify-end gap-xs">
						<Button size="large" disabled={deciding || locked} onClick={decline}>
							Not now
						</Button>
						<OWButton
							size="large"
							type="primary"
							loading={deciding}
							disabled={locked}
							onClick={send}
						>
							Send request
						</OWButton>
					</div>
				</>
			)}
		</div>
	);
};

export default OscarTeamRequestCard;
